// Google Cloud Storage Image Service (client-side)
// Delegates uploads to backend to avoid exposing tokens

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export interface UploadResult {
  success: boolean;
  url?: string;
  error?: string;
}

export interface TestResult {
  success: boolean;
  message: string;
  details?: any;
}

class GoogleCloudStorageService {
  private bucketName: string;
  private projectId: string;
  private apiBase: string; // Backend base URL

  constructor() {
    this.bucketName = import.meta.env.VITE_GCS_BUCKET_NAME || '';
    this.projectId = import.meta.env.VITE_GCS_PROJECT_ID || '';

    const envBase = (import.meta.env as any).VITE_API_BASE_URL as string | undefined;
    console.log('[GCS Service] VITE_API_BASE_URL from env:', envBase, 'hostname:', typeof window !== 'undefined' ? window.location.hostname : 'N/A');
    if (envBase && envBase.trim().length > 0) {
      this.apiBase = envBase.trim().replace(/\/$/, '');
    } else if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
      this.apiBase = 'http://localhost:3002';
    } else {
      this.apiBase = '';
    }
    console.log('[GCS Service] Final apiBase:', this.apiBase);
  }

  private isConfigured(): boolean {
    return !!(this.bucketName && this.projectId);
  }

  private validateFile(file: File): { isValid: boolean; error?: string } {
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      return { isValid: false, error: 'File size exceeds 10MB limit' };
    }
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      return { isValid: false, error: 'Invalid file type. Only JPEG, PNG, GIF, and WebP images are allowed' };
    }
    return { isValid: true };
  }

  async uploadImage(file: File, onProgress?: (progress: UploadProgress) => void): Promise<UploadResult> {
    try {
      const validation = this.validateFile(file);
      if (!validation.isValid) {
        return { success: false, error: validation.error };
      }
      if (!this.isConfigured()) {
        return { success: false, error: 'Google Cloud Storage not configured. Please check environment variables.' };
      }

      const form = new FormData();
      form.append('file', file, file.name);
      form.append('folder', 'car-images');

      const endpoint = this.apiBase ? `${this.apiBase}/api/storage/upload` : '/api/storage/upload';
      console.log('[GCS Upload] apiBase:', this.apiBase, 'endpoint:', endpoint, 'hostname:', window.location.hostname);
      const response = await fetch(endpoint, { method: 'POST', body: form });
      if (!response.ok) {
        const text = await response.text();
        return { success: false, error: `Upload failed: ${response.status} ${text}` };
      }
      const data = await response.json();
      if (onProgress) {
        onProgress({ loaded: 1, total: 1, percentage: 100 });
      }
      return { success: true, url: data.url };
    } catch (error) {
      return { success: false, error: `Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}` };
    }
  }

  async uploadMultipleImages(files: File[], onProgress?: (progress: UploadProgress) => void): Promise<UploadResult[]> {
    const results: UploadResult[] = [];
    for (let i = 0; i < files.length; i++) {
      const result = await this.uploadImage(files[i], (p) => {
        if (onProgress) {
          const overall = {
            loaded: (i * 100) + p.percentage,
            total: files.length * 100,
            percentage: Math.round(((i * 100) + p.percentage) / files.length)
          };
          onProgress(overall);
        }
      });
      results.push(result);
    }
    return results;
  }

  async testConnection(): Promise<TestResult> {
    try {
      if (!this.isConfigured()) {
        return { success: false, message: 'Google Cloud Storage not configured', details: 'Check env vars' };
      }
      const healthUrl = this.apiBase ? `${this.apiBase}/health` : '/health';
      const res = await fetch(healthUrl);
      if (res.ok) {
        const details = await res.json().catch(() => ({}));
        return { success: true, message: 'Backend reachable', details };
      }
      return { success: false, message: `Backend not reachable: ${res.status}` };
    } catch (error) {
      return { success: false, message: 'Connection test failed', details: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  async deleteFile(filename: string): Promise<boolean> {
    try {
      const endpoint = this.apiBase ? `${this.apiBase}/api/storage/delete?filename=${encodeURIComponent(filename)}` : `/api/storage/delete?filename=${encodeURIComponent(filename)}`;
      const res = await fetch(endpoint, { method: 'DELETE' });
      return res.ok;
    } catch (error) {
      console.error('Delete file error:', error);
      return false;
    }
  }

  getConfigInfo() {
    return {
      provider: 'Google Cloud Storage',
      bucketName: this.bucketName,
      projectId: this.projectId,
      apiBase: this.apiBase,
      isConfigured: this.isConfigured()
    };
  }
}

export const googleCloudStorageService = new GoogleCloudStorageService();
export default googleCloudStorageService;